import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { isBefore, startOfDay, getDay, format, addDays } from 'date-fns';

// Party calendar with availability dots. Reuses the react-day-picker v8
// modifiersClassNames availability-dot pattern from the Bookings page for
// visual consistency. Day hints reflect party-level availability (all
// standard rigs free = open, some booked = limited, all booked = full).
export default function PartyCalendar({
  selectedDate,
  onSelectDate,
  visibleMonth,
  onMonthChange,
  dayHints,
  openDays,
}) {
  const openDaysArr = openDays && openDays.length ? openDays : [0, 1, 2, 3, 4, 5, 6];

  // Build arrays of Date objects for each hint category, for the current
  // visible month + a few days either side (react-day-picker renders a
  // 42-cell grid that bleeds into adjacent months).
  const hintArrays = React.useMemo(() => {
    const base = visibleMonth || new Date();
    const start = addDays(new Date(base.getFullYear(), base.getMonth(), 1), -7);
    const result = { open: [], limited: [], full: [] };
    for (let i = 0; i < 49; i++) {
      const d = addDays(start, i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const dow = d.getDay();
      if (!openDaysArr.includes(dow)) continue; // closed days get no dot
      const hint = dayHints ? dayHints(dateStr) : 'open';
      if (hint === 'open') result.open.push(d);
      else if (hint === 'limited') result.limited.push(d);
      else if (hint === 'full') result.full.push(d);
    }
    return result;
  }, [dayHints, visibleMonth, openDaysArr]);

  return (
    <div>
      <p className="text-primary text-xs font-bold tracking-widest mb-4 flex items-center gap-2">
        <CalendarIcon className="w-4 h-4" /> SELECT DATE
      </p>
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={(d) => { onSelectDate(d); }}
        month={visibleMonth}
        onMonthChange={onMonthChange}
        disabled={(date) => isBefore(date, startOfDay(new Date())) || !openDaysArr.includes(getDay(date))}
        modifiers={{
          'avail-open': hintArrays.open,
          'avail-limited': hintArrays.limited,
          'avail-full': hintArrays.full,
        }}
        modifiersClassNames={{
          'avail-open': 'day-avail-open',
          'avail-limited': 'day-avail-limited',
          'avail-full': 'day-avail-full',
        }}
        className="border border-white/10 bg-[#111115] p-4 w-fit"
      />
      <div className="flex items-center gap-4 mt-3 text-[10px] text-white/30">
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> All rigs free</span>
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Some booked</span>
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Full</span>
      </div>
    </div>
  );
}